import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import {
  ASSIGN_PLAN_TTL_MS,
  findTopic,
  localDateAt,
  localTimeAt,
  localToInstant,
  makeAssignPlan,
  normalizeTimeZone,
  unservedStudents,
  HOMEWORK_DEFAULT_TITLE,
  type AssignPlan,
  type HomeworkSource,
  type PlanSummary,
  type StudentSummary,
} from "@edu/shared";
import * as api from "../../lib/api.ts";
import { dayCountText } from "../../lib/plurals.ts";
import { Crumbs } from "../shell/Crumbs.tsx";
import { useSession } from "../../lib/session.tsx";
import { AssignActions } from "./assign/AssignActions.tsx";
import { AssignPreview } from "./assign/AssignPreview.tsx";
import { DueAndTitle } from "./assign/DueAndTitle.tsx";
import { MaterialBlock } from "./assign/MaterialBlock.tsx";
import { LessonImages, PlanImages, useSourceImages } from "./assign/SourceImages.tsx";
import {
  hasTaskText,
  materialBlock,
  materialCounts,
  materialFileIds,
  materialMediaRefs,
  materialPreviewLine,
  restoreThumbs,
} from "./assign/material.ts";
import { repeatOf } from "./assign/repeat.ts";
import { sourceBlockText, sourceOrder } from "./assign/source-order.ts";
import { useMaterial } from "./assign/use-material.ts";
import { ExerciseSource } from "./assign/ExerciseSource.tsx";
import { ResumeOffer } from "./assign/ResumeOffer.tsx";
import { AssignResult } from "./assign/AssignResult.tsx";
import { StudentPicker } from "./assign/StudentPicker.tsx";
import { newId } from "./assign/new-id.ts";
import { dropPlan, sweepPlans } from "./assign/plan-storage.ts";
import { previewTasks } from "./assign/preview-tasks.ts";
import { topicOrder, type SourceKind } from "./assign/subjects.ts";
import { useAssignRun } from "./assign/use-assign-run.ts";
import { usePlanSource } from "./assign/use-plan-source.ts";
import { useTopicSources } from "./assign/use-topic-sources.ts";
import { useUrlPicks } from "./assign/use-url-picks.ts";

/** Срок жизни черновика в днях: подпись про удалённый черновик считает его, а не пишет число. */
const PLAN_TTL_DAYS = Math.round(ASSIGN_PLAN_TTL_MS / 86_400_000);

/**
 * Форма «Задать домашнюю» (раздел 5 docs/homework-phase2-design.md).
 *
 * Целевой сценарий тот же, что в фазе 1: у преподавателя пять минут между
 * уроками. Изменилось то, что получателей может быть много: одна и та же
 * домашка выдаётся сразу нескольким за один заход, и это экономит около 29
 * нажатий из 42 на шести учениках.
 *
 * Порядок блоков это порядок принятия решений, и первым стоит самое опасное:
 * выбор получателя единственное необратимое решение формы. Неверный уровень
 * правится пересозданием, а домашка, ушедшая не тому человеку, уже прочитана.
 *
 * Раскладка с 25.09.2026 (просьба владельца: «лучше использовать пространство
 * экрана, цель десктоп») двухколоночная: слева решения (кому, задания), справа
 * липкая колонка с тем, что читают перед нажатием (срок и название,
 * предпросмотр, кнопка, результат). ⚠️ Порядок в разметке остался порядком
 * решений: на узком экране колонки встают друг под друга, и блоки идут ровно в
 * прежней очерёдности (5.3 проекта), первым самое опасное.
 *
 * ⚠️ Форма НЕ уходит на карточку созданной работы: разбор в `AssignResult`.
 * ⚠️ Ключи идемпотентности живут в `localStorage`: разбор в `assign/plan-storage.ts`.
 * ⚠️ Реестр генераторов сюда не попадает ни одним путём: темы, уровни, потолки и
 * названия приезжают справочником с сервера (5.11 фазы 2).
 */

export function AssignHomeworkPage() {
  const { state: sessionState } = useSession();
  const [params] = useSearchParams();
  const location = useLocation();
  const urlStudentId = params.get("studentId");
  const urlLessonId = params.get("lessonId");
  /** Второй вход: состав заданий пришёл адресом (6.3 docs/student-errors-design.md). */
  const urlPicksRaw = params.get("picks");

  const [students, setStudents] = useState<StudentSummary[] | null>(null);
  const [plans, setPlans] = useState<PlanSummary[] | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set(urlStudentId === null ? [] : [urlStudentId]),
  );

  /**
   * Источник ровно один, смешения нет (решение владельца 17.08.2026, 5.5).
   * Состав в адресе сразу ставит его в «По темам»: пришедший из панели «Где
   * дыры» человек заказал именно темы, и показать ему заготовку значит спрятать
   * то, за чем он нажимал.
   */
  const [source, setSource] = useState<SourceKind>(urlPicksRaw === null ? "plan" : "oge");
  const planSource = usePlanSource(params.get("planId") ?? "");
  /** Справочники обоих предметов и тот из них, что сейчас открыт. */
  const topicSources = useTopicSources(source);
  const topics = topicSources.active;

  /** Текст задания и картинки (docs/homework-scratch-design.md). */
  const material = useMaterial();

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("18:00");
  const [dateTouched, setDateTouched] = useState(false);

  const [err, setErr] = useState<string | null>(null);
  const [resume, setResume] = useState<AssignPlan | null>(null);
  const [expired, setExpired] = useState(0);
  /**
   * Состав из адреса: строки, подпись про порядок и слова про непоместившееся.
   *
   * ⚠️ Всегда в МАТЕМАТИКУ, а не в открытую ветку: ссылку кладёт панель «Где
   * дыры», а она сегодня про математику, и в английской ветке состав лёг бы
   * чужими слагами. Второму предмету понадобится свой параметр адреса, а не
   * догадка формы о том, что сейчас открыто.
   */
  const urlPicks = useUrlPicks({
    raw: urlPicksRaw,
    topics: topicSources.bySubject.math,
    // Название ставится только в пустое поле: набранное руками не затирается
    onTitle: (name) => setTitle((prev) => (prev === "" ? name : prev)),
  });
  const topicName = (skill: string): string | null =>
    findTopic(topics.topics ?? [], skill)?.name ?? null;
  /** Ход раздачи: цикл, прогресс, судьба плана и слова отказов. */
  const run = useAssignRun({ topicName });

  const timeZone = useMemo(() => {
    if (sessionState.status !== "signed") return null;
    const raw = sessionState.me.timeZone;
    return typeof raw === "string" ? normalizeTimeZone(raw) : null;
  }, [sessionState]);

  useEffect(() => {
    void (async () => {
      try {
        const [list, planList] = await Promise.all([api.listStudents(), api.listPlans()]);
        setStudents(list.students);
        setPlans(planList.plans);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  /**
   * Уборка планов и предложение продолжить незаконченную раздачу. ⚠️ Удалённый
   * по сроку план называется вслух: молчаливое удаление это то же самое
   * обещание, которого никто не выполнил.
   */
  const viewing = sessionState.status === "signed" && Boolean(sessionState.me.viewer);
  useEffect(() => {
    // В просмотре кабинета админом планы в этом браузере его собственные: ни
    // убирать, ни предлагать их продолжить под чужим кабинетом нельзя
    if (viewing) return;
    const swept = sweepPlans(Date.now());
    // Обе ветки источника теперь восстанавливаются, поэтому отбора по виду
    // источника здесь больше нет: до шага Б4 план по темам форме нечем было
    // показать, и предлагать продолжение, которое не откроется, было хуже
    setResume(swept.pending[0] ?? null);
    setExpired(swept.expired);
  }, [viewing]);

  /**
   * Выбранные и выбираемые. ⚠️ Это единственное место, где отвязанный
   * отсеивается из выбора: пикер рисует плашки из этого же списка и своего
   * фильтра не держит, иначе два фильтра разъедутся на первой правке.
   */
  const chosen = (students ?? []).filter((s) => selected.has(s.userId) && !s.unlinked);
  const singleId = chosen.length === 1 ? (chosen[0]?.userId ?? null) : null;
  const wantsDue = date !== "";

  /**
   * ⚠️ `lessonId` принадлежит ПАРЕ, а не раздаче: сервер проверяет, что урок
   * этого ученика, и в цикле по двенадцати чужой урок дал бы одиннадцать
   * отказов `lesson_not_found`. Поэтому подпись едет только тому, из карточки
   * урока которого пришли, и только когда выбран он один.
   */
  const lessonId = urlLessonId !== null && singleId !== null && singleId === urlStudentId
    ? urlLessonId
    : null;
  const lessonNote =
    urlLessonId !== null && lessonId === null
      ? "Подпись «задана после занятия» ставится только одному ученику, поэтому здесь она снята."
      : null;

  /**
   * «С доски урока» живёт ровно там, где живёт подпись урока: форма открыта из
   * карточки занятия, и получатель ровно один, ученик этого урока (1.2
   * docs/homework-scratch-design.md). Добавил получателей, лента пропадает, а
   * отмеченное остаётся: это файлы самого преподавателя.
   */
  const lessonImages = useSourceImages(lessonId, api.listLessonImages);
  const planImages = useSourceImages(
    source === "plan" && planSource.planId !== "" ? planSource.planId : null,
    api.listPlanImages,
  );

  /**
   * «Задать ещё раз» с карточки выданной домашки (1.7 проекта): текст задания и
   * картинки приезжают состоянием перехода, а не адресом (текст до 10 000
   * знаков в адрес не помещается). Применяется один раз на заход.
   */
  const repeat = useMemo(() => repeatOf(location.state), [location.state]);
  const { restore: restoreMaterial } = material;
  useEffect(() => {
    if (repeat === null) return;
    restoreMaterial(repeat.taskText, restoreThumbs(repeat.fileIds, repeat.media, repeat.posters));
  }, [repeat, restoreMaterial]);

  /** Предзаполнение срока ближайшим занятием: только пока получатель один. */
  useEffect(() => {
    if (singleId === null || timeZone === null || dateTouched) return;
    let alive = true;
    void (async () => {
      try {
        const next = await api.nextOccurrence(singleId);
        if (!alive || next.at === null) return;
        setDate(localDateAt(next.at, timeZone));
        setTime(localTimeAt(next.at, timeZone));
      } catch {
        // Предзаполнение это удобство: без него поле просто пустое
      }
    })();
    return () => {
      alive = false;
    };
  }, [singleId, timeZone, dateTouched]);

  const dueAt = useMemo(() => {
    if (!wantsDue || timeZone === null) return null;
    return localToInstant(date, time, timeZone).at;
  }, [wantsDue, date, time, timeZone]);

  /**
   * Заказ заданий: ровно один источник, остальные в этот момент не
   * спрашиваются. Источника может не быть вовсе, но только когда он явно не
   * выбран (разбор в `assign/source-order.ts`).
   */
  const sourceState = sourceOrder({
    source,
    planId: planSource.planId,
    planLoaded: planSource.plan !== null,
    planFailed: planSource.plan === null && planSource.err !== null,
    pickedIds: planSource.picked.map((ex) => ex.id),
    topicOrder: source !== "plan" && topics.ready ? topicOrder(source, topics.picks) : null,
  });
  const order: HomeworkSource | null = sourceState.kind === "order" ? sourceState.order : null;
  const sourceBlocked = sourceState.kind === "blocked" ? sourceState.reason : null;

  const fileIds = materialFileIds(material.items);
  const mediaRefs = materialMediaRefs(material.items);
  const withText = hasTaskText(material.taskText);
  const blocked = materialBlock(material.items);
  /**
   * Заданий может не быть вовсе: домашка из текста задания и картинок законна
   * (1.4 проекта). Пустой не бывает: хоть что-то из трёх обязано быть.
   */
  const hasContent = order !== null || withText || fileIds.length > 0;

  const submit = (): void => {
    if (!hasContent || blocked !== null || sourceBlocked !== null || chosen.length === 0) return;
    if (wantsDue && timeZone === null) {
      setErr("Чтобы поставить срок, подтвердите часовой пояс на экране расписания.");
      return;
    }
    /**
     * ⚠️ Каждое нажатие минтит НОВЫЕ идентификаторы. Человек, задающий вторую
     * тему тем же двенадцати, иначе получил бы 200 со старыми работами и решил,
     * что фича сломалась.
     */
    const fresh = makeAssignPlan({
      students: chosen.map((s) => ({ userId: s.userId, displayName: s.displayName })),
      source: order ?? undefined,
      taskText: withText ? material.taskText : undefined,
      materialFileIds: fileIds.length > 0 ? fileIds : undefined,
      materialMedia: mediaRefs.length > 0 ? mediaRefs : undefined,
      dueAt,
      title: title.trim() === "" ? null : title.trim(),
      lessonId,
      now: Date.now(),
      newId,
    });
    run.start(fresh);
  };

  /** Продолжение брошенной раздачи: те же ключи, значит повтор безопасен. */
  const continuePlan = (saved: AssignPlan): void => {
    // Продолжение сильнее адреса: состав из ссылки строк больше не тронет,
    // иначе приехавший позже справочник затёр бы восстановленную раздачу
    urlPicks.drop();
    setSelected(new Set(saved.students.map((s) => s.userId)));
    const savedSource = saved.source;
    if (savedSource !== undefined) {
      setSource(savedSource.kind);
      if (savedSource.kind === "plan") {
        planSource.restore(savedSource.planId, savedSource.elementIds ?? null);
      } else {
        topicSources.restore(savedSource.kind, savedSource.picks);
      }
    }
    // Миниатюры восстанавливаются по файлам: подписанных путей в плане нет,
    // они живут минуты, а план до 30 суток
    material.restore(
      saved.taskText ?? "",
      restoreThumbs(saved.materialFileIds ?? [], saved.materialMedia ?? []),
    );
    setTitle(saved.title ?? "");
    setDateTouched(true);
    if (saved.dueAt !== null && timeZone !== null) {
      setDate(localDateAt(saved.dueAt, timeZone));
      setTime(localTimeAt(saved.dueAt, timeZone));
    } else {
      setDate("");
    }
    run.restore(saved);
    setResume(null);
  };

  const names = useMemo(
    () => new Map((students ?? []).map((s) => [s.userId, s.displayName])),
    [students],
  );
  const urlStudent = (students ?? []).find((s) => s.userId === urlStudentId) ?? null;
  /**
   * Строка под «Кому» про ученика из адреса, которого в поле нет. Только пока
   * поле пустое: выбравшему кого-то вручную история ссылки уже не нужна.
   *
   * ⚠️ Имя называется, а родовых окончаний нет: «отвязан» или «отвязана»
   * форма не знает, пола в модели нет.
   */
  const linkNote =
    urlStudentId === null || students === null || chosen.length > 0
      ? null
      : urlStudent === null
        ? { text: "Этого ученика нет в вашем списке, выберите вручную.", error: false }
        : urlStudent.unlinked
          ? {
              text: `${urlStudent.displayName}: ссылка отозвана. Новую домашку не задать, пока не отправите ему новую ссылку.`,
              error: true,
            }
          : null;
  /**
   * Раздача не доведена до конца. ⚠️ Кнопка «Задать» при этом НЕ блокируется:
   * задать вторую работу тем же ученикам законно. Но нажатие на неё выдаст
   * новые ключи, то есть тем девяти, кому работа уже создана, приедет вторая, и
   * сказать об этом надо до нажатия, а не после.
   */
  const unfinished =
    run.plan !== null && !run.busy && run.allFailure === null && unservedStudents(run.plan).length > 0;
  const listHref = urlStudentId === null ? "/homework" : `/homework?studentId=${urlStudentId}`;
  const total = chosen.length;
  const showErr = err ?? (source === "plan" ? planSource.err : null);

  /** Что уйдёт ученику: у заготовки и у генераторов это разные обещания. */
  const tasks = previewTasks(order, planSource, topics);
  const materialLine = materialPreviewLine({
    taskText: material.taskText,
    ...materialCounts(material.items),
  });

  /**
   * Подпись про порядок строк нужна только листу из адреса: он собран по темам,
   * на которых стоит названная, и «по квадратным уравнениям» начинается с
   * дробей (4.1 проекта). У заготовки порядок не про это.
   */
  const orderNote = source === "oge" ? urlPicks.orderNote : null;

  return (
    <main className="home home--list assign">
      {/* Место главного действия пустое: «Задать» стоит в подвале формы, и
          вторая копия наверху означала бы две главные кнопки на экране */}
      <header className="head">
        <Crumbs trail={[{ label: "Домашка", to: "/homework" }]} here="Задать домашку" />
        <div className="head__line">
          <h1 className="head__h">Задать домашку</h1>
        </div>
      </header>

      {showErr !== null && <p className="err">{showErr}</p>}
      {expired > 0 && (
        <p className="muted">
          Черновик старше {dayCountText(PLAN_TTL_DAYS)} удалён, задайте домашку заново.
        </p>
      )}
      {/*
        Потеря строк из ссылки называется вслух и один раз. Молчаливо короткий
        лист читается как «форма перепутала», а отказом это быть не может: ссылку
        открывают из закладки через полгода (6.3 проекта)
      */}
      {urlPicks.lost !== null && <p className="muted">{urlPicks.lost}</p>}
      {run.storageBroken && (
        <p className="err">
          Этот браузер не сохраняет черновики: не закрывайте вкладку, пока домашка не задана.
        </p>
      )}

      {resume !== null && (
        <ResumeOffer
          plan={resume}
          onContinue={() => continuePlan(resume)}
          onDrop={() => {
            dropPlan(resume.planRunId);
            setResume(null);
          }}
        />
      )}

      {/*
        Две колонки включаются только на широком экране (`.assign__grid` в
        styles.css). Решения слева, чтение перед нажатием справа
      */}
      <div className="assign__grid">
        <div className="assign__main">
          <StudentPicker
            students={students}
            chosen={chosen}
            linkNote={linkNote}
            busy={run.busy}
            onToggle={(userId) =>
              setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(userId)) next.delete(userId);
                else next.add(userId);
                return next;
              })
            }
            onPickMany={(ids) => setSelected((prev) => new Set([...prev, ...ids]))}
            onClear={() => setSelected(new Set())}
          />

          <MaterialBlock
            material={material}
            busy={run.busy}
            lessonImages={<LessonImages view={lessonImages} material={material} />}
          />

          <ExerciseSource
            kind={source}
            onKind={setSource}
            busy={run.busy}
            plans={plans ?? []}
            planId={planSource.planId}
            planLoaded={planSource.plan !== null}
            answerable={planSource.answerable}
            excluded={planSource.excluded}
            onPlanId={planSource.setPlanId}
            onToggle={planSource.toggle}
            topics={topicSources.bySubject}
            planImages={<PlanImages view={planImages} material={material} />}
          />
        </div>
        <div className="assign__side">
          <DueAndTitle
            date={date}
            time={time}
            title={title}
            timeZone={timeZone}
            titleHint={
              order === null
                ? HOMEWORK_DEFAULT_TITLE
                : source === "plan"
                  ? (planSource.plan?.title ?? "из названия заготовки")
                  : "из названий тем"
            }
            onDate={(value) => {
              setDateTouched(true);
              setDate(value);
            }}
            onTime={(value) => {
              setDateTouched(true);
              setTime(value);
            }}
            onTitle={setTitle}
          />

          {total > 0 && hasContent && (
            <AssignPreview
              names={chosen.map((s) => s.displayName)}
              tasks={tasks}
              materialLine={materialLine}
              dueAt={dueAt}
              timeZone={timeZone}
              lessonNote={lessonNote}
              orderNote={orderNote}
            />
          )}

          <AssignActions
            total={total}
            disabled={
              run.busy ||
              !hasContent ||
              blocked !== null ||
              sourceBlocked !== null ||
              total === 0 ||
              (wantsDue && timeZone === null)
            }
            note={
              sourceBlocked !== null
                ? sourceBlockText(sourceBlocked)
                : blocked === "uploading"
                ? "«Задать» станет доступна, когда картинки загрузятся."
                : blocked === "failed"
                  ? "Одна из картинок не загрузилась: повторите или уберите её."
                  : null
            }
            busy={run.busy}
            progress={run.progress}
            targets={run.targets}
            counter={
              source === "plan" && planSource.plan !== null
                ? `заданий: ${planSource.picked.length} из ${planSource.answerable.length}`
                : null
            }
            unfinished={unfinished}
            allFailure={run.allFailure}
            onSubmit={submit}
          />

          {run.plan !== null && !run.busy && run.allFailure === null && (
            <AssignResult
              plan={run.plan}
              names={names}
              outcomes={run.outcomes}
              busy={run.busy}
              listHref={listHref}
              onRetry={run.retry}
            />
          )}
        </div>
      </div>
    </main>
  );
}
